import {
  Autocomplete,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Modal from 'components/shared/Modal';
import React, { SyntheticEvent, useCallback, useContext, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { defaultFormProps, getRequiredValidationRule } from 'util/Form';
import { defaultGridContainerProps, defaultGridItemProps, defaultSnackbarErrorProps } from 'util/Layout';
import { ModalProps } from 'store/types/ComponentProps';
import CompanyService from 'services/api/CompanyService';
import { useSnackbar } from 'notistack';
import JobFunction from 'store/types/JobFunction';
import CompanyUser from 'store/types/CompanyUser';
import UserNameFormSection from 'components/profile/UserNameFormSection';
import EmailFormItem from 'components/profile/EmailFormItem';
import PhoneFormItem from 'components/profile/PhoneFormItem';
import { CompanyUserFormValues } from 'store/types/FormValues';
import { UserContext } from 'components/UserGuard';
import TitleFormItem from 'components/profile/TitleFormItem';
import SelectOption from 'store/types/SelectOption';
import { MemberClassification } from 'store/types/Company';
import DirectoryStatus from 'store/enums/DirectoryStatus';

import commonStyles from 'styles/common.module.scss';
import styles from './EditCompanyUserModal.module.scss';

interface EditCompanyUserModalProps extends ModalProps {
  data: CompanyUser;
  jobFunctionsList?: JobFunction[];
  refetch: () => void;
  companyId: string;
  memberClassification?: MemberClassification[];
  directoryStatusOptions?: SelectOption[];
}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

type ViewCompanyUserFormValues = CompanyUserFormValues;

const getDefaultValues = (
  { firstName, lastName, phone, email, middleInitial, title, directoryNetworks, directoryStatus }: CompanyUser,
  hideDirectoryStatusId?: string
): ViewCompanyUserFormValues => ({
  firstName,
  lastName,
  middleInitial,
  phone,
  email,
  title,
  directoryNetworks: directoryNetworks ?? [],
  directoryStatus: directoryStatus || hideDirectoryStatusId || '',
  functionIds: [],
});

const getSelectedLabel = (value: string[] = [], options: MemberClassification[] = []): React.ReactNode =>
  value.length
    ? options
        .filter(({ id }) => value.includes(id))
        .map(({ name }) => name)
        .join(', ')
    : '';

const EditCompanyUserModal: React.FunctionComponent<EditCompanyUserModalProps> = ({
  open,
  onClose,
  refetch,
  data,
  companyId,
  jobFunctionsList,
  memberClassification,
  directoryStatusOptions,
}) => {
  const hideDirectoryStatusId = useMemo(
    () => directoryStatusOptions?.find((ds) => ds.name === DirectoryStatus.Hide)?.id,
    [directoryStatusOptions]
  );
  const submitDirectoryStatusId = useMemo(
    () => directoryStatusOptions?.find((ds) => ds.name === DirectoryStatus.Submit)?.id,
    [directoryStatusOptions]
  );
  const approvedDirectoryStatusId = useMemo(
    () => directoryStatusOptions?.find((ds) => ds.name === DirectoryStatus.Approved)?.id,
    [directoryStatusOptions]
  );
  const defaultValues = useMemo(() => getDefaultValues(data, hideDirectoryStatusId), [data, hideDirectoryStatusId]);
  const { id } = useContext(UserContext);
  const form = useForm<ViewCompanyUserFormValues>({ ...defaultFormProps, defaultValues });
  const { handleSubmit, formState, control, reset, setValue, watch } = form;
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [deletedCustomerFunctionIds, setDeletedCustomerFunctionIds] = useState<string[]>([]);

  const { directoryStatus, functionIds = [] } = watch();

  const activeCustomerFunctions = useMemo(
    () => data.functions?.filter((customerFunction) => !deletedCustomerFunctionIds.includes(customerFunction.id)) ?? [],
    [data.functions, deletedCustomerFunctionIds]
  );

  const availableJobFunctions = useMemo(() => {
    const existingJobFunctionIds = new Set(
      data.functions?.map((customerFunction) => customerFunction.functionId) ?? []
    );

    return jobFunctionsList?.filter((jobFunction) => !existingJobFunctionIds.has(jobFunction.id)) ?? [];
  }, [data.functions, jobFunctionsList]);

  const hasCustomerFunctionChanges = Boolean(functionIds.length || deletedCustomerFunctionIds.length);
  const hasEmployeeChanges = Object.keys(formState.dirtyFields).some((fieldName) => fieldName !== 'jobFunctionIds');

  const handleClose = useCallback(() => {
    onClose();
    reset(defaultValues);
    setDeletedCustomerFunctionIds([]);
  }, [defaultValues, onClose, reset]);

  const handleDeleteModalOpen = useCallback(() => {
    setDeleteModalOpen(true);
  }, []);

  const handleDeleteModalClose = useCallback(() => {
    setDeleteModalOpen(false);
  }, []);

  const handleDeleteButtonClick = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault();
      if (data) {
        setLoading(true);
        CompanyService.removeCompanyAdmin(companyId, data.id)
          .then(() => {
            enqueueSnackbar('Person successfully deleted', { variant: 'success' });
            setLoading(false);
            refetch();
            handleClose();
          })
          .catch((errorMessage) => {
            setLoading(false);
            enqueueSnackbar(errorMessage, defaultSnackbarErrorProps);
          });
      }
    },
    [companyId, data, enqueueSnackbar, handleClose, refetch]
  );

  const handleUpdateUserSubmit = useCallback(
    ({ functionIds: addedFunctionIds, ...formValues }: ViewCompanyUserFormValues) => {
      if (data) {
        setLoading(true);

        CompanyService.updateCompanyAdminUser(
          companyId,
          data.id,
          hasEmployeeChanges ? formValues : null,
          addedFunctionIds ?? [],
          deletedCustomerFunctionIds
        )
          .then(() => {
            enqueueSnackbar('Person successfully updated', { variant: 'success' });
            setLoading(false);
            refetch();
            handleClose();
          })
          .catch((errorMessage) => {
            setLoading(false);
            enqueueSnackbar(errorMessage, defaultSnackbarErrorProps);
          });
      }
    },
    [companyId, data, deletedCustomerFunctionIds, enqueueSnackbar, handleClose, hasEmployeeChanges, refetch]
  );

  const handleCustomerFunctionDelete = useCallback((customerFunctionId: string) => {
    setDeletedCustomerFunctionIds((currentIds) =>
      currentIds.includes(customerFunctionId) ? currentIds : [...currentIds, customerFunctionId]
    );
  }, []);

  const handleDirectoryStatusClick = useCallback(
    (status: DirectoryStatus) => {
      const statusId = directoryStatusOptions?.find((option) => option.name === status)?.id;

      setValue('directoryStatus', statusId, {
        shouldDirty: true,
        shouldTouch: true,
      });

      if (status === DirectoryStatus.Hide) {
        setValue('directoryNetworks', [], {
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    },
    [directoryStatusOptions, setValue]
  );

  const isDirectoryPending = Boolean(submitDirectoryStatusId) && submitDirectoryStatusId === data.directoryStatus;
  const isDirectoryApproved = Boolean(approvedDirectoryStatusId) && approvedDirectoryStatusId === data.directoryStatus;
  const showDirectoryNetworks = directoryStatus !== hideDirectoryStatusId;

  return (
    <>
      <FormProvider {...form}>
        <Modal
          open={open}
          onClose={handleClose}
          maxWidth={'sm'}
          title={'Edit Person'}
          loading={loading}
          actions={
            <>
              <div>
                <Button color={'secondary'} variant={'outlined'} onClick={handleClose}>
                  {'Cancel'}
                </Button>
                {id !== data?.id && (
                  <Button
                    color={'secondary'}
                    variant={'outlined'}
                    onClick={handleDeleteModalOpen}
                    className={styles.submitButton}
                  >
                    {'Remove Person'}
                  </Button>
                )}
              </div>
              <Button
                color={'secondary'}
                variant={'contained'}
                type={'submit'}
                disabled={!formState.isValid || (!hasEmployeeChanges && !hasCustomerFunctionChanges)}
                onClick={handleSubmit(handleUpdateUserSubmit)}
              >
                {'Save'}
              </Button>
            </>
          }
        >
          <Grid {...defaultGridContainerProps}>
            {data.entityId && (
              <Grid {...defaultGridItemProps}>
                <p className={styles.id}>{`ID: ${data.entityId}`}</p>
              </Grid>
            )}
            <UserNameFormSection middleNameVisible={true} />
            <Grid {...defaultGridItemProps} md={6}>
              <EmailFormItem disabled={true} />
            </Grid>
            <Grid {...defaultGridItemProps} md={6}>
              <PhoneFormItem />
            </Grid>
            <Grid {...defaultGridItemProps} md={6}>
              <TitleFormItem />
            </Grid>
            <Grid {...defaultGridItemProps}>
              <FormControl>
                <FormLabel sx={{ mb: 1 }}>Directory Status</FormLabel>
                <Stack direction={'row'} gap={2}>
                  <Button
                    variant={directoryStatus === hideDirectoryStatusId ? 'contained' : 'outlined'}
                    color={'primary'}
                    onClick={() => handleDirectoryStatusClick(DirectoryStatus.Hide)}
                  >
                    {DirectoryStatus.Hide}
                  </Button>
                  {isDirectoryApproved ? (
                    <Button variant={'contained'} color={'success'} disabled>
                      {DirectoryStatus.Approved}
                    </Button>
                  ) : isDirectoryPending ? (
                    <Button variant={'contained'} color={'warning'} disabled>
                      {DirectoryStatus.Pending}
                    </Button>
                  ) : (
                    <Button
                      variant={directoryStatus === submitDirectoryStatusId ? 'contained' : 'outlined'}
                      color={'primary'}
                      onClick={() => handleDirectoryStatusClick(DirectoryStatus.Submit)}
                    >
                      {DirectoryStatus.Submit}
                    </Button>
                  )}
                </Stack>
              </FormControl>
            </Grid>
            {showDirectoryNetworks && (
              <Grid {...defaultGridItemProps} xl={6}>
                <Controller
                  render={({ field }) => (
                    <TextField
                      {...field}
                      className={styles.input}
                      label={'Directory Networks'}
                      disabled={loading}
                      select
                      required
                      SelectProps={{
                        displayEmpty: true,
                        multiple: true,
                        renderValue: (selected: any) => getSelectedLabel(selected || [], memberClassification),
                      }}
                    >
                      {memberClassification?.map(({ id, name }) => (
                        <MenuItem value={id} key={`directory-network-option-${id}-${name}`}>
                          <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            checked={field.value?.includes(id)}
                            color={'primary'}
                            size={'small'}
                          />
                          <ListItemText primary={name} />
                        </MenuItem>
                      )) ?? []}
                    </TextField>
                  )}
                  name={'directoryNetworks'}
                  control={control}
                  rules={{
                    required: getRequiredValidationRule('directory networks', showDirectoryNetworks),
                  }}
                />
              </Grid>
            )}
            <Grid {...defaultGridItemProps}>
              <Divider sx={{ mb: 2, mt: 1 }} />
              <Typography variant={'h6'} sx={{ mb: 1 }}>
                {'Manage Permissions'}
              </Typography>

              <FormLabel>{'Portal Roles'}</FormLabel>
              <Stack divider={<Divider flexItem />} sx={{ mb: 2 }}>
                {activeCustomerFunctions.length ? (
                  activeCustomerFunctions.map((customerFunction) => (
                    <Stack
                      key={customerFunction.id}
                      direction={'row'}
                      alignItems={'center'}
                      justifyContent={'space-between'}
                      sx={{ minHeight: 48, py: 0.5 }}
                    >
                      <Typography>{customerFunction.functionName}</Typography>
                      {id !== data?.id && (
                        <IconButton
                          aria-label={`Remove ${customerFunction.functionName} portal role`}
                          color={'error'}
                          disabled={loading}
                          size={'small'}
                          onClick={() => handleCustomerFunctionDelete(customerFunction.id)}
                        >
                          <DeleteOutlineIcon fontSize={'small'} />
                        </IconButton>
                      )}
                    </Stack>
                  ))
                ) : (
                  <Typography color={'text.secondary'} sx={{ py: 1.5 }}>
                    {'No portal roles assigned.'}
                  </Typography>
                )}
              </Stack>

              <Controller
                name={'functionIds'}
                control={control}
                render={({ field }) => {
                  const selectedOptions = availableJobFunctions.filter(({ id }) => field.value?.includes(id));

                  return (
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      disabled={loading || !availableJobFunctions.length}
                      options={availableJobFunctions}
                      value={selectedOptions}
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      onBlur={field.onBlur}
                      onChange={(_event, selectedRoles) => {
                        field.onChange(selectedRoles.map(({ id }) => id));
                      }}
                      ChipProps={{
                        color: 'secondary',
                        variant: 'filled',
                      }}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox checked={selected} checkedIcon={checkedIcon} icon={icon} sx={{ mr: 1 }} />
                          {option.name}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          inputRef={field.ref}
                          label={'Add Portal Roles'}
                          placeholder={selectedOptions.length ? undefined : 'Select one or more Portal Roles'}
                        />
                      )}
                    />
                  );
                }}
              />
            </Grid>
          </Grid>
        </Modal>
      </FormProvider>
      <Modal
        title={'Remove Person'}
        loading={loading}
        open={deleteModalOpen}
        maxWidth={'xs'}
        actions={
          <>
            <Button color={'secondary'} variant={'outlined'} onClick={handleDeleteModalClose}>
              {'Cancel'}
            </Button>
            <Button
              type={'submit'}
              variant={'contained'}
              onClick={handleDeleteButtonClick}
              className={commonStyles.dangerButtonContained}
            >
              {'Remove'}
            </Button>
          </>
        }
      >
        <p className={commonStyles.text}>{'Are you sure you want to remove this person?'}</p>
      </Modal>
    </>
  );
};

export default EditCompanyUserModal;
